// src/pages/student-portal/certificates/CertificatesPage.js
// Student Portal version (purple theme)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    Button,
    Chip,
    Skeleton,
    CircularProgress,
    Alert,
    Pagination,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
} from '@mui/material';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SortIcon from '@mui/icons-material/Sort';
import VerifiedIcon from '@mui/icons-material/Verified';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#8b5cf6';

function formatDate(dateStr) {
    if (!dateStr) return 'N/A';
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return dateStr;
    }
}

// ─── Stat Card ───────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, title, value, color, loading }) => (
    <Card
        sx={{
            height: '100%',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
                borderColor: color,
                boxShadow: `0 4px 20px ${color}20`,
            },
        }}
    >
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: '14px',
                        bgcolor: `${color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Icon sx={{ color, fontSize: 28 }} />
                </Box>
                <Box>
                    <Typography
                        variant="body2"
                        sx={{ fontFamily, color: '#64748b', fontWeight: 500, fontSize: '0.8rem' }}
                    >
                        {title}
                    </Typography>
                    {loading ? (
                        <Skeleton variant="text" width={60} sx={{ fontSize: '1.5rem' }} />
                    ) : (
                        <Typography
                            variant="h5"
                            sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}
                        >
                            {value}
                        </Typography>
                    )}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ─── Certificate Card (horizontal layout) ───────────────────────────────────

const CertificateCard = ({ certificate, onView, onDownload, onVerifyLink }) => {
    const isValid = certificate.is_valid !== false;
    const statusLabel = isValid ? 'Valid' : 'Expired';
    const statusColor = isValid ? '#10b981' : '#ef4444';
    const statusBg = isValid ? '#dcfce7' : '#fee2e2';

    const courseThumbnail = certificate.course?.thumbnail_url;
    const courseTitle = certificate.course?.title || 'Untitled Course';
    const courseCategory = certificate.course?.category || '';

    return (
        <Card
            sx={{
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
                    borderColor: ACCENT,
                },
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
                {/* Thumbnail */}
                <Box
                    sx={{
                        width: { xs: '100%', sm: 200 },
                        minHeight: { xs: 140, sm: 'auto' },
                        flexShrink: 0,
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    {courseThumbnail ? (
                        <Box
                            component="img"
                            src={courseThumbnail}
                            alt={courseTitle}
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <MilitaryTechIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.7)' }} />
                        </Box>
                    )}
                    {/* Status badge overlaid on thumbnail */}
                    <Chip
                        label={statusLabel}
                        size="small"
                        icon={
                            isValid
                                ? <CheckCircleIcon sx={{ fontSize: 14, color: `${statusColor} !important` }} />
                                : <CancelIcon sx={{ fontSize: 14, color: `${statusColor} !important` }} />
                        }
                        sx={{
                            position: 'absolute',
                            top: 10,
                            left: 10,
                            fontFamily,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            bgcolor: statusBg,
                            color: statusColor,
                            height: 26,
                            border: '1px solid',
                            borderColor: `${statusColor}30`,
                            '& .MuiChip-icon': { ml: '6px' },
                        }}
                    />
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, p: 2.5, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Box>
                        {/* Category */}
                        {courseCategory && (
                            <Typography
                                variant="caption"
                                sx={{
                                    fontFamily,
                                    color: ACCENT,
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    fontSize: '0.65rem',
                                    mb: 0.5,
                                    display: 'block',
                                }}
                            >
                                {courseCategory}
                            </Typography>
                        )}

                        {/* Title */}
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontFamily,
                                fontWeight: 700,
                                color: '#1e293b',
                                lineHeight: 1.3,
                                mb: 1,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {courseTitle}
                        </Typography>

                        {/* Certificate Number */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                                Certificate No:
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontFamily,
                                    fontWeight: 600,
                                    color: '#475569',
                                    fontFeatureSettings: '"tnum"',
                                }}
                            >
                                {certificate.certificate_number || 'N/A'}
                            </Typography>
                        </Box>

                        {/* Dates row */}
                        <Box sx={{ display: 'flex', gap: 3, mt: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CalendarTodayIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                                <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                                    Issued:
                                </Typography>
                                <Typography variant="caption" sx={{ fontFamily, fontWeight: 600, color: '#475569' }}>
                                    {formatDate(certificate.issued_at)}
                                </Typography>
                            </Box>
                            {certificate.expires_at && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarTodayIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                                    <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                                        Expires:
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontFamily,
                                            fontWeight: 600,
                                            color: isValid ? '#475569' : '#ef4444',
                                        }}
                                    >
                                        {formatDate(certificate.expires_at)}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Action buttons */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                            onClick={() => onView(certificate)}
                            sx={{
                                fontFamily,
                                fontWeight: 600,
                                textTransform: 'none',
                                bgcolor: ACCENT,
                                borderRadius: '10px',
                                boxShadow: 'none',
                                fontSize: '0.75rem',
                                px: 2,
                                '&:hover': { bgcolor: '#7c3aed', boxShadow: 'none' },
                            }}
                        >
                            View
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                            onClick={() => onDownload(certificate)}
                            sx={{
                                fontFamily,
                                fontWeight: 600,
                                textTransform: 'none',
                                borderColor: '#e2e8f0',
                                color: '#475569',
                                borderRadius: '10px',
                                fontSize: '0.75rem',
                                px: 2,
                                '&:hover': { borderColor: ACCENT, color: ACCENT, bgcolor: `${ACCENT}08` },
                            }}
                        >
                            Download
                        </Button>
                        <IconButton
                            size="small"
                            onClick={() => onVerifyLink(certificate)}
                            sx={{
                                border: '1px solid #e2e8f0',
                                borderRadius: '10px',
                                color: '#64748b',
                                '&:hover': { borderColor: ACCENT, color: ACCENT, bgcolor: `${ACCENT}08` },
                            }}
                        >
                            <OpenInNewIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Box>
                </Box>
            </Box>
        </Card>
    );
};

// ─── Loading Skeleton ────────────────────────────────────────────────────────

const CertificateCardSkeleton = () => (
    <Card
        sx={{
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
            overflow: 'hidden',
        }}
    >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
            <Skeleton
                variant="rectangular"
                sx={{ width: { xs: '100%', sm: 200 }, height: { xs: 140, sm: 160 } }}
            />
            <Box sx={{ flex: 1, p: 2.5 }}>
                <Skeleton variant="text" width="30%" sx={{ fontSize: '0.65rem', mb: 0.5 }} />
                <Skeleton variant="text" width="70%" sx={{ fontSize: '1.1rem', mb: 1 }} />
                <Skeleton variant="text" width="50%" sx={{ fontSize: '0.8rem', mb: 0.5 }} />
                <Skeleton variant="text" width="60%" sx={{ fontSize: '0.8rem', mb: 2 }} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Skeleton variant="rounded" width={70} height={32} sx={{ borderRadius: '10px' }} />
                    <Skeleton variant="rounded" width={90} height={32} sx={{ borderRadius: '10px' }} />
                    <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: '10px' }} />
                </Box>
            </Box>
        </Box>
    </Card>
);

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function CertificatesPage() {
    const navigate = useNavigate();

    const [certificates, setCertificates] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, per_page: 10, total: 0, total_pages: 1 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [sortBy, setSortBy] = useState('issued_at');
    const [sortOrder, setSortOrder] = useState('desc');

    // Verify modal state
    const [verifyOpen, setVerifyOpen] = useState(false);
    const [verifyCertNum, setVerifyCertNum] = useState('');
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [verifyResult, setVerifyResult] = useState(null);
    const [verifyError, setVerifyError] = useState('');

    // ─── Fetch certificates ──────────────────────────────────────────────

    const fetchCertificates = useCallback(async (page = 1) => {
        setLoading(true);
        setError('');
        try {
            const response = await axiosInstance.get('/api/lms/portal/certificates', {
                params: {
                    page,
                    per_page: pagination.per_page,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                },
            });
            const data = response.data;
            setCertificates(data.certificates || []);
            if (data.pagination) {
                setPagination((prev) => ({
                    ...prev,
                    page: data.pagination.page || page,
                    total: data.pagination.total || 0,
                    total_pages: data.pagination.total_pages || 1,
                }));
            }
        } catch (err) {
            console.error('Failed to fetch certificates:', err);
            setError(err.response?.data?.message || 'Failed to load certificates.');
        } finally {
            setLoading(false);
        }
    }, [sortBy, sortOrder, pagination.per_page]);

    useEffect(() => {
        fetchCertificates(1);
    }, [fetchCertificates]);

    // ─── Stats ───────────────────────────────────────────────────────────

    const totalCerts = pagination.total || certificates.length;
    const validCerts = certificates.filter((c) => c.is_valid !== false).length;
    const latestDate = certificates.length > 0
        ? formatDate(
            [...certificates].sort(
                (a, b) => new Date(b.issued_at || 0) - new Date(a.issued_at || 0)
            )[0]?.issued_at
        )
        : 'N/A';

    // ─── Handlers ────────────────────────────────────────────────────────

    const handlePageChange = (_, page) => {
        fetchCertificates(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSortBy = (field) => {
        if (sortBy === field) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };

    const handleView = useCallback((cert) => {
        navigate(`/student-portal/certificates/${cert.id}`);
    }, [navigate]);

    const handleDownload = useCallback((cert) => {
        if (cert.download_url) {
            window.open(cert.download_url, '_blank');
        }
    }, []);

    const handleVerifyLink = useCallback((cert) => {
        if (cert.verification_url) {
            window.open(cert.verification_url, '_blank');
        }
    }, []);

    // ─── Verify Certificate Modal ────────────────────────────────────────

    const handleOpenVerify = () => {
        setVerifyOpen(true);
        setVerifyCertNum('');
        setVerifyResult(null);
        setVerifyError('');
    };

    const handleCloseVerify = () => {
        setVerifyOpen(false);
        setVerifyCertNum('');
        setVerifyResult(null);
        setVerifyError('');
    };

    const handleVerifyCertificate = async () => {
        if (!verifyCertNum.trim()) return;
        setVerifyLoading(true);
        setVerifyResult(null);
        setVerifyError('');
        try {
            const response = await axiosInstance.post('/api/lms/portal/certificates/verify', {
                certificate_number: verifyCertNum.trim(),
            });
            setVerifyResult(response.data);
        } catch (err) {
            console.error('Verify failed:', err);
            if (err.response?.status === 404) {
                setVerifyResult({ verified: false, message: err.response?.data?.message || 'Certificate not found.' });
            } else {
                setVerifyError(err.response?.data?.message || 'Verification failed. Please try again.');
            }
        } finally {
            setVerifyLoading(false);
        }
    };

    // ─── Render ──────────────────────────────────────────────────────────

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <MilitaryTechIcon sx={{ fontSize: 40, color: 'white' }} />
                        <Box>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontFamily,
                                    fontWeight: 800,
                                    color: 'white',
                                    fontSize: { xs: '1.5rem', md: '2rem' },
                                }}
                            >
                                My Certificates
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily,
                                    color: 'rgba(255,255,255,0.85)',
                                    fontSize: '0.95rem',
                                }}
                            >
                                Your earned certificates and achievements
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<VerifiedIcon />}
                        onClick={handleOpenVerify}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            borderRadius: '12px',
                            px: 3,
                            py: 1,
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            boxShadow: 'none',
                            '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.3)',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        Verify Certificate
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* Stat Cards */}
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <StatCard
                            icon={MilitaryTechIcon}
                            title="Total Certificates"
                            value={totalCerts}
                            color="#10b981"
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <StatCard
                            icon={VerifiedIcon}
                            title="Valid"
                            value={validCerts}
                            color="#3b82f6"
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <StatCard
                            icon={CalendarTodayIcon}
                            title="Latest Issued"
                            value={latestDate}
                            color={ACCENT}
                            loading={loading}
                        />
                    </Grid>
                </Grid>

                {/* Sort Controls */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 3,
                        flexWrap: 'wrap',
                    }}
                >
                    <SortIcon sx={{ fontSize: 20, color: '#64748b' }} />
                    <Typography
                        variant="body2"
                        sx={{ fontFamily, color: '#64748b', fontWeight: 500, mr: 0.5 }}
                    >
                        Sort by:
                    </Typography>
                    <Button
                        size="small"
                        variant={sortBy === 'issued_at' ? 'contained' : 'outlined'}
                        onClick={() => handleSortBy('issued_at')}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '10px',
                            fontSize: '0.8rem',
                            px: 2,
                            boxShadow: 'none',
                            ...(sortBy === 'issued_at'
                                ? {
                                    bgcolor: ACCENT,
                                    color: 'white',
                                    '&:hover': { bgcolor: '#7c3aed', boxShadow: 'none' },
                                }
                                : {
                                    borderColor: '#e2e8f0',
                                    color: '#475569',
                                    '&:hover': { borderColor: ACCENT, color: ACCENT, bgcolor: `${ACCENT}08` },
                                }),
                        }}
                    >
                        Date {sortBy === 'issued_at' ? (sortOrder === 'desc' ? '\u2193' : '\u2191') : ''}
                    </Button>
                    <Button
                        size="small"
                        variant={sortBy === 'course_title' ? 'contained' : 'outlined'}
                        onClick={() => handleSortBy('course_title')}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '10px',
                            fontSize: '0.8rem',
                            px: 2,
                            boxShadow: 'none',
                            ...(sortBy === 'course_title'
                                ? {
                                    bgcolor: ACCENT,
                                    color: 'white',
                                    '&:hover': { bgcolor: '#7c3aed', boxShadow: 'none' },
                                }
                                : {
                                    borderColor: '#e2e8f0',
                                    color: '#475569',
                                    '&:hover': { borderColor: ACCENT, color: ACCENT, bgcolor: `${ACCENT}08` },
                                }),
                        }}
                    >
                        Course Name {sortBy === 'course_title' ? (sortOrder === 'desc' ? '\u2193' : '\u2191') : ''}
                    </Button>
                </Box>

                {/* Error */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3, borderRadius: '12px', fontFamily }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={() => fetchCertificates(pagination.page)}
                                sx={{ fontFamily, textTransform: 'none' }}
                            >
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}

                {/* Loading State */}
                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <CertificateCardSkeleton key={i} />
                        ))}
                    </Box>
                ) : certificates.length === 0 ? (
                    /* Empty State */
                    <Paper
                        sx={{
                            py: 8,
                            px: 3,
                            textAlign: 'center',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: 'none',
                        }}
                    >
                        <MilitaryTechIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                        <Typography
                            variant="h6"
                            sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 1 }}
                        >
                            No certificates yet
                        </Typography>
                        <Typography sx={{ fontFamily, color: '#64748b', mb: 3 }}>
                            Complete courses to earn certificates. Start learning today!
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/student-portal/courses')}
                            sx={{
                                fontFamily,
                                fontWeight: 600,
                                textTransform: 'none',
                                bgcolor: ACCENT,
                                borderRadius: '12px',
                                px: 4,
                                boxShadow: 'none',
                                '&:hover': { bgcolor: '#7c3aed', boxShadow: 'none' },
                            }}
                        >
                            Browse Courses
                        </Button>
                    </Paper>
                ) : (
                    <>
                        {/* Certificate Cards */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {certificates.map((cert) => (
                                <CertificateCard
                                    key={cert.id}
                                    certificate={cert}
                                    onView={handleView}
                                    onDownload={handleDownload}
                                    onVerifyLink={handleVerifyLink}
                                />
                            ))}
                        </Box>

                        {/* Pagination */}
                        {pagination.total_pages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                <Pagination
                                    count={pagination.total_pages}
                                    page={pagination.page}
                                    onChange={handlePageChange}
                                    shape="rounded"
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            fontFamily,
                                            fontWeight: 600,
                                            borderRadius: '10px',
                                        },
                                        '& .Mui-selected': {
                                            bgcolor: `${ACCENT} !important`,
                                            color: 'white',
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* ─── Verify Certificate Modal ─────────────────────────────────── */}
            <Dialog
                open={verifyOpen}
                onClose={handleCloseVerify}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        overflow: 'hidden',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        fontFamily,
                        fontWeight: 700,
                        fontSize: '1.25rem',
                        color: '#1e293b',
                        pb: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                    }}
                >
                    <Box
                        sx={{
                            p: 1,
                            borderRadius: '12px',
                            bgcolor: `${ACCENT}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <VerifiedIcon sx={{ color: ACCENT, fontSize: 24 }} />
                    </Box>
                    Verify Certificate
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Typography
                        sx={{ fontFamily, color: '#64748b', fontSize: '0.9rem', mb: 2.5, mt: 1 }}
                    >
                        Enter a certificate number to verify its authenticity.
                    </Typography>
                    <TextField
                        fullWidth
                        label="Certificate Number"
                        placeholder="e.g., CERT-2024-XXXX"
                        value={verifyCertNum}
                        onChange={(e) => setVerifyCertNum(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleVerifyCertificate();
                        }}
                        InputProps={{
                            startAdornment: (
                                <SearchIcon sx={{ color: '#94a3b8', mr: 1, fontSize: 20 }} />
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontFamily,
                                borderRadius: '12px',
                                '&.Mui-focused fieldset': { borderColor: ACCENT },
                            },
                            '& .MuiInputLabel-root': {
                                fontFamily,
                                '&.Mui-focused': { color: ACCENT },
                            },
                        }}
                    />

                    {/* Verify Error */}
                    {verifyError && (
                        <Alert severity="error" sx={{ mt: 2, borderRadius: '10px', fontFamily }}>
                            {verifyError}
                        </Alert>
                    )}

                    {/* Verify Result */}
                    {verifyResult && (
                        <Paper
                            sx={{
                                mt: 2.5,
                                p: 2.5,
                                borderRadius: '14px',
                                border: '1px solid',
                                borderColor: verifyResult.verified ? '#10b98130' : '#ef444430',
                                bgcolor: verifyResult.verified ? '#f0fdf4' : '#fef2f2',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: verifyResult.verified ? 2 : 0 }}>
                                {verifyResult.verified ? (
                                    <CheckCircleIcon sx={{ color: '#10b981', fontSize: 28 }} />
                                ) : (
                                    <CancelIcon sx={{ color: '#ef4444', fontSize: 28 }} />
                                )}
                                <Typography
                                    sx={{
                                        fontFamily,
                                        fontWeight: 700,
                                        color: verifyResult.verified ? '#166534' : '#991b1b',
                                        fontSize: '1rem',
                                    }}
                                >
                                    {verifyResult.verified ? 'Certificate Verified' : 'Certificate Not Found'}
                                </Typography>
                            </Box>

                            {verifyResult.verified && verifyResult.certificate && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 5.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" sx={{ fontFamily, color: '#64748b' }}>
                                            Holder
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                            {verifyResult.certificate.student?.full_name || 'N/A'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" sx={{ fontFamily, color: '#64748b' }}>
                                            Course
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                            {verifyResult.certificate.course?.title || 'N/A'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" sx={{ fontFamily, color: '#64748b' }}>
                                            Issued
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                            {formatDate(verifyResult.certificate.issued_at)}
                                        </Typography>
                                    </Box>
                                    {verifyResult.certificate.expires_at && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="caption" sx={{ fontFamily, color: '#64748b' }}>
                                                Expires
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                                {formatDate(verifyResult.certificate.expires_at)}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" sx={{ fontFamily, color: '#64748b' }}>
                                            Status
                                        </Typography>
                                        <Chip
                                            label={verifyResult.certificate.is_valid !== false ? 'Valid' : 'Expired'}
                                            size="small"
                                            sx={{
                                                fontFamily,
                                                fontWeight: 600,
                                                fontSize: '0.65rem',
                                                height: 20,
                                                bgcolor: verifyResult.certificate.is_valid !== false ? '#dcfce7' : '#fee2e2',
                                                color: verifyResult.certificate.is_valid !== false ? '#10b981' : '#ef4444',
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}

                            {!verifyResult.verified && verifyResult.message && (
                                <Typography
                                    sx={{ fontFamily, color: '#991b1b', fontSize: '0.85rem', mt: 1, pl: 5.5 }}
                                >
                                    {verifyResult.message}
                                </Typography>
                            )}
                        </Paper>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
                    <Button
                        onClick={handleCloseVerify}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            color: '#64748b',
                            borderRadius: '10px',
                        }}
                    >
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleVerifyCertificate}
                        disabled={!verifyCertNum.trim() || verifyLoading}
                        startIcon={
                            verifyLoading
                                ? <CircularProgress size={16} sx={{ color: 'white' }} />
                                : <SearchIcon />
                        }
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            bgcolor: ACCENT,
                            borderRadius: '10px',
                            px: 3,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#7c3aed', boxShadow: 'none' },
                            '&.Mui-disabled': { bgcolor: `${ACCENT}60`, color: 'white' },
                        }}
                    >
                        {verifyLoading ? 'Verifying...' : 'Verify'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
